import { useMediaQuery, Box, useTheme } from '@material-ui/core'
import {
  DocumentShare,
  EuDocument,
  EuOrganization,
  RequestContactSettingFieldValue,
  Visitor
} from '@noco/http-client/lib/noco'
import { destroyCookie } from 'nookies'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ModalAuthentication } from 'src/components/modals/ModalAuthentication'
import { ModalVisitorConnectToContact } from 'src/components/modals/ModalVisitorConnectToContact'
import { CookieAgreement } from 'src/components/molecules/CookieAgreement'
import { DocumentSlideDetail } from 'src/components/organisms/DocumentSlideDetail'
import {
  useSendAuthenticationCode,
  useUnifiedFormSignin,
  useVerifyAuthenticationCode
} from 'src/fixtures/modules/contactAuth/hooks'
import {
  useGetContactDocumentUnifiedShareForm,
  useGetCotnactDocumentShare
} from 'src/fixtures/modules/contactDocument/hooks'
import { extractEmailFromRequestValues } from 'src/fixtures/modules/contact/utils'
import { useGetVisitor } from 'src/fixtures/modules/visitor/hooks'
import { getTokenFromCookie } from 'src/fixtures/utils'

export interface PageDocumentShareWithLinkProps {
  document?: EuDocument
  visitor?: Visitor
  organization?: EuOrganization
  documentId: string
  documentShareId: string
  siteShareId?: string
  siteId?: string
  documentShare?: DocumentShare
  hasMaterialContactable?: boolean
  canRequestToExtendExpiredAt?: boolean
  expiredOn?: string | null
}

export const PageDocumentShareWithLink = ({
  documentId,
  documentShare,
  documentShareId,
  document,
  organization,
  siteId,
  visitor,
  siteShareId,
  canRequestToExtendExpiredAt,
  expiredOn
}: PageDocumentShareWithLinkProps) => {
  const theme = useTheme()
  const isPCView = useMediaQuery(theme.breakpoints.up('sm'))
  const { showCookieAgreement } = getTokenFromCookie()
  const [openCookie, setOpenCookie] = useState<boolean>(false)

  const { mutate: mutateDocumentShare } = useGetCotnactDocumentShare({ documentShareId, siteShareId })
  const { mutate: mutateVisitor } = useGetVisitor()
  const { handleVerifyAuthCode } = useVerifyAuthenticationCode()
  const { handleUnifiedFormSignin } = useUnifiedFormSignin()
  const { handleSendAuthCode } = useSendAuthenticationCode()
  const resendingRef = useRef(false)
  const lastResendAtRef = useRef(0)
  const { data: unifiedShareFormsRes, mutate: mutateUnifiedShareForms } = useGetContactDocumentUnifiedShareForm(
    documentShareId,
    siteShareId
  )

  const [openVerifyCodeModal, setOpenVerifyCodeModal] = useState(false)
  const [lastSubmittedValues, setLastSubmittedValues] = useState<RequestContactSettingFieldValue[] | undefined>()
  const [lastSubmittedEmail, setLastSubmittedEmail] = useState('')
  const [isInit, setIsInit] = useState(false)

  const isFormActive = documentShare?.isFormActive
  const shareForms = unifiedShareFormsRes?.shareForms
  const noForms = useMemo(() => !!shareForms && shareForms.length === 0, [shareForms])
  const isActivated = useMemo(() => !isFormActive || noForms, [isFormActive, noForms])
  const openUnifiedForm = useMemo(
    () => !!isFormActive && !noForms && !openVerifyCodeModal && !!shareForms,
    [isFormActive, noForms, openVerifyCodeModal, shareForms]
  )

  const handleCookieClick = useCallback(() => {
    setOpenCookie(false)
    destroyCookie(null, 'showCookieAgreement', { path: '/' })
  }, [])

  useEffect(() => {
    if (showCookieAgreement) setOpenCookie(true)
  }, [showCookieAgreement])

  const syncDocument = useCallback(() => {
    setOpenVerifyCodeModal(false)
    mutateDocumentShare()
    setTimeout(() => {
      mutateVisitor().then(() => {
        mutateUnifiedShareForms()
      })
    }, 128)
  }, [mutateDocumentShare, mutateUnifiedShareForms, mutateVisitor])

  const handleSubmitUnifiedForm = useCallback(
    async (values: RequestContactSettingFieldValue[] | undefined) => {
      if (!values) return
      const email = extractEmailFromRequestValues(values, shareForms) ?? ''
      const result = await handleUnifiedFormSignin({
        documentId,
        siteId,
        contactSettingFieldValues: values
      })
      setLastSubmittedValues(values)
      if (email) setLastSubmittedEmail(email)
      if (result?.status === 'require_auth_code') {
        setOpenVerifyCodeModal(true)
      } else if (result?.status === 'skip_auth') {
        syncDocument()
      }
    },
    [documentId, handleUnifiedFormSignin, shareForms, siteId, syncDocument]
  )

  const handleResendAuthCode = useCallback(async () => {
    if (!lastSubmittedValues || resendingRef.current) return
    if (Date.now() - lastResendAtRef.current < 5000) return
    resendingRef.current = true
    lastResendAtRef.current = Date.now()
    try {
      await handleSendAuthCode({
        documentId,
        siteId,
        contactSettingFieldValues: lastSubmittedValues
      })
    } finally {
      resendingRef.current = false
    }
  }, [documentId, handleSendAuthCode, lastSubmittedValues, siteId])

  const onSubmitModalAuthentication = useCallback(
    (code: string) => {
      handleVerifyAuthCode({ code })
        .then(() => syncDocument())
        .catch(() => {
          // 失敗時は認証コードモーダルに留まる。ユーザーは再入力 or 再送信で対応
        })
    },
    [handleVerifyAuthCode, syncDocument]
  )

  useEffect(() => {
    if (visitor && document) {
      setIsInit(true)
    }
  }, [visitor, document, documentShare, unifiedShareFormsRes])

  return (
    <>
      <Box width="100vw" height={isPCView ? '100vh' : '100%'}>
        {document && organization && (
          <DocumentSlideDetail
            isActivated={!!isActivated}
            documentShareId={documentShareId}
            siteShareId={siteShareId}
            policySettings={organization.policySettings}
            document={document}
            organization={organization}
            isPCView={isPCView}
            canRequestToExtendExpiredAt={canRequestToExtendExpiredAt}
            visitor={visitor}
            siteId={siteId}
            expiredOn={expiredOn}
          />
        )}
      </Box>
      {/* 統合フォームモーダル */}
      {isInit && (
        <ModalVisitorConnectToContact
          organizationName={organization?.name}
          policySettings={organization?.policySettings}
          documentId={documentId}
          siteId={siteId}
          open={openUnifiedForm}
          onClose={() => {}}
          onSubmit={handleSubmitUnifiedForm}
          documentShareForms={shareForms}
          materialTitle={document?.title}
        />
      )}
      {/* 認証コード入力モーダル */}
      <ModalAuthentication
        open={openVerifyCodeModal}
        email={lastSubmittedEmail}
        resend={handleResendAuthCode}
        onSubmit={onSubmitModalAuthentication}
        onClose={() => {}}
      />
      <CookieAgreement open={openCookie} onClick={handleCookieClick} />
    </>
  )
}
